import { HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { v4 as uuidv4 } from 'uuid';

/**
 * Extended HTTP request with request ID for tracing
 */
interface HttpRequestWithRequestId extends HttpRequest<unknown> {
  __requestId?: string;
}

/**
 * Request ID Interceptor
 *
 * Automatically generates and adds X-Request-Id header to all outgoing requests
 * for distributed tracing and debugging. Each request gets a unique UUID that
 * can be used to trace the request through logs and error reports.
 *
 * Features:
 * - Unique UUID generation for each request
 * - Preserves existing request IDs if present
 * - Supports request correlation in microservices
 * - Helps with debugging and log correlation
 */
export const requestIdInterceptor: HttpInterceptorFn = (req, next) => {
  // Check if request already has a request ID
  if (req.headers.has('X-Request-Id')) {
    // Preserve existing request ID (useful for retry scenarios)
    return next(req);
  }

  // Generate a new UUID for this request
  const requestId = uuidv4();

  // Clone the request and add the request ID header
  const requestWithId = req.clone({
    setHeaders: {
      'X-Request-Id': requestId
    }
  }) as HttpRequestWithRequestId;

  // Store request ID in context for error handling and logging
  // This can be accessed by error handlers and logging services
  requestWithId.__requestId = requestId;

  return next(requestWithId);
};